/**
 * End-to-end VLAN path check (OP#1818) -- types for GET /sites/{id}/vlan-path and the pure logic
 * the hop-chain view uses. Backend: services/vlan_path.py; contract: docs/design/vlan-path-check.md.
 *
 * WHY (Jedi Home, 2026-10-12/13): an SSID on VLAN 1000 was on air, the phone associated, and DHCP
 * never answered -- VLAN 1000 was missing on the SonicFi RSW226G behind the Edgecore switches.
 * Every device was green. The check walks SSID -> AP -> switch ports (LLDP) -> inter-switch links
 * -> gateway port(s) -> gateway network/DHCP/WAN forwarding and names the FIRST hop that is
 * broken (proven) or unknown (cannot be checked, e.g. a SonicFi switch until OP#1820).
 *
 * DISPLAY RULE: the chain reads like the frame's way home, gateway on the LEFT, SSID on the RIGHT
 * ("gateway <- switch:port <- switch:port <- AP <- SSID"). UNKNOWN is never drawn as OK and never
 * as broken: it gets the warn ramp and the reason. Pure and dependency-free (runs under
 * `node --test`, see vlanPath.test.ts).
 */

export type PathStatus = "ok" | "broken" | "unknown" | "na";
export type HopId = "H1" | "H2" | "H3" | "H4" | "H5";
/** The status ramp tone a hop renders with (StatusPill / .md-status). */
export type PathTone = "healthy" | "critical" | "warn" | "info";

export interface PathDeviceRef {
  serial: string | null;
  name: string | null;
  kind: "ap" | "switch" | "gateway" | string;
}

export interface PathPeerRef {
  serial: string | null;
  name: string | null;
  kind?: string;
  port?: string | null;
  port_label?: string | null;
}

/** One hop row (services/vlan_path._hop). */
export interface PathHop {
  hop: HopId;
  name: string;
  status: PathStatus;
  device: PathDeviceRef;
  port: string | null;
  /** Faceplate (one-based) port number, e.g. "19" for Ethernet18. */
  port_label: string | null;
  peer: PathPeerRef | null;
  vlan: number | null;
  expected: string | null;
  actual: string | null;
  /** device-read | state | config | traffic | meridian-intent | owgw-config */
  source: string | null;
  observed_at: string | null;
  age_seconds: number | null;
  /** "12 min ago" (server-formatted). */
  age: string | null;
  code: string;
  reason: string | null;
  /** The suggested resolution (e.g. the audited "Read VLANs from switch X" POST). */
  action: { label: string; method: string; path: string } | null;
  mode?: "tagged" | "untagged";
  link_source?: string | null;
}

export interface PathApResult {
  ap: PathDeviceRef;
  vlan: number | null;
  mode: "tagged" | "untagged";
  status: PathStatus;
  summary: string;
  hops: PathHop[];
  first_broken: PathHop | null;
  first_unknown: PathHop | null;
}

/** GET /sites/{id}/vlan-path?vlan=N | ?ssid=name */
export interface VlanPathResult {
  site_id: number;
  vlan: number | null;
  ssid: string | null;
  mode: "tagged" | "untagged";
  status: PathStatus;
  summary: string;
  aps: PathApResult[];
  gateways: PathHop[];
  not_carrying: PathDeviceRef[];
  counts: { ok: number; broken: number; unknown: number };
  first_broken: PathHop | null;
  first_unknown: PathHop | null;
  checked_at: string;
  sources_note?: string;
}

/** The compact form on a rollout verification / SSID dry run / rollout plan. */
export interface VlanPathCompact {
  vlan: number | null;
  status: PathStatus;
  summary: string;
  counts: { ok: number; broken: number; unknown: number } | null;
  first_broken: PathHop | null;
  first_unknown: PathHop | null;
}

/** rollout.verification.path_check (OP#1818, after every rollout). */
export interface RolloutPathCheck {
  checked_at?: string;
  status: PathStatus | "error";
  error?: string;
  vlans: Record<string, VlanPathCompact>;
}

/** Status -> ramp tone. ok=healthy, broken=critical, unknown=warn, n/a=info. */
export function pathTone(status: PathStatus | "error" | string): PathTone {
  if (status === "ok") return "healthy";
  if (status === "broken" || status === "error") return "critical";
  if (status === "unknown") return "warn";
  return "info";
}

/** The short pill label for a result. */
export function pathLabel(status: PathStatus | "error" | string): string {
  switch (status) {
    case "ok":
      return "Path OK";
    case "broken":
      return "Path broken";
    case "unknown":
      return "Cannot fully check";
    case "error":
      return "Check failed";
    default:
      return "Not carried";
  }
}

const RANK: Record<string, number> = { broken: 3, unknown: 2, ok: 1, na: 0 };

/** The worst of several statuses (broken > unknown > ok > na). */
export function worstStatus(statuses: PathStatus[]): PathStatus {
  let worst: PathStatus = "na";
  for (const s of statuses) if ((RANK[s] ?? 0) > (RANK[worst] ?? 0)) worst = s;
  return worst;
}

/** One node of the horizontal chain. */
export interface ChainNode {
  key: string;
  /** "JH-ECS2100" / "SSID JH-IoT-1000" */
  title: string;
  /** "port 1" / "ports 1, 2" / "AP" / "gateway" */
  sub: string | null;
  status: PathStatus;
  /** The hop rows behind this node (for the details panel). */
  hops: PathHop[];
  kind: "gateway" | "switch" | "ap" | "ssid";
}

function portSub(hops: PathHop[]): string | null {
  const labels = hops.map((h) => h.port_label).filter((p): p is string => !!p);
  if (!labels.length) return null;
  return labels.length === 1 ? `port ${labels[0]}` : `ports ${labels.join(", ")}`;
}

/**
 * The chain for one AP, gateway first: [gateway(s)] [switch:port facing the gateway] [link ends
 * from the gateway side down] [AP port] [AP] [SSID]. An HA pair collapses into one gateway node
 * (worst status) and the gateway-facing ports of one switch into one node.
 */
export function chainForAp(ap: PathApResult, ssid?: string | null): ChainNode[] {
  const nodes: ChainNode[] = [];
  const h5 = ap.hops.filter((h) => h.hop === "H5");
  if (h5.length) {
    nodes.push({
      key: "gw",
      title: h5.map((h) => h.device.name ?? "gateway").join(" + "),
      sub: h5.length > 1 ? "gateway pair" : "gateway",
      status: worstStatus(h5.map((h) => h.status)),
      hops: h5,
      kind: "gateway",
    });
  }
  // H4: group by switch.
  const h4 = ap.hops.filter((h) => h.hop === "H4");
  const bySwitch = new Map<string, PathHop[]>();
  for (const h of h4) {
    const k = h.device.serial ?? h.device.name ?? "?";
    bySwitch.set(k, [...(bySwitch.get(k) ?? []), h]);
  }
  for (const [k, hs] of bySwitch) {
    const first = hs[0]!; // a group is created with its first hop, never empty
    nodes.push({
      key: `h4-${k}`,
      title: first.device.name ?? k,
      sub: portSub(hs),
      status: worstStatus(hs.map((h) => h.status)),
      hops: hs,
      kind: first.device.kind === "gateway" ? "gateway" : "switch",
    });
  }
  // H3 ends arrive AP-side first; the chain is drawn gateway-side first.
  const h3 = ap.hops.filter((h) => h.hop === "H3").reverse();
  h3.forEach((h, i) => {
    nodes.push({
      key: `h3-${i}-${h.device.serial}-${h.port}`,
      title: h.device.name ?? "switch",
      sub: h.port_label ? `port ${h.port_label}` : "port ?",
      status: h.status,
      hops: [h],
      kind: "switch",
    });
  });
  for (const h of ap.hops.filter((x) => x.hop === "H2")) {
    nodes.push({
      key: `h2-${h.device.serial}-${h.port}`,
      title: h.device.kind === "ap" ? "uplink" : h.device.name ?? "switch",
      sub: h.port_label ? `port ${h.port_label}` : h.device.kind === "ap" ? "not known" : null,
      status: h.status,
      hops: [h],
      kind: "switch",
    });
  }
  const h1 = ap.hops.find((h) => h.hop === "H1");
  nodes.push({ key: "ap", title: ap.ap.name ?? "AP", sub: "AP", status: "na", hops: [], kind: "ap" });
  if (h1) {
    nodes.push({
      key: "ssid",
      title: ssid ? `SSID ${ssid}` : `VLAN ${ap.vlan ?? "?"}`,
      sub: ap.vlan != null ? `VLAN ${ap.vlan} ${ap.mode}` : ap.mode,
      status: h1.status,
      hops: [h1],
      kind: "ssid",
    });
  }
  return nodes;
}

/** "device-read, 12 min ago" -- the evidence of a hop, or null. */
export function hopEvidence(h: PathHop): string | null {
  if (!h.source) return null;
  return h.age ? `${h.source}, ${h.age}` : h.source;
}

/** "switch RSW226G port 19" / "gateway JH-GW-01" -- where a hop is. */
export function hopPlace(h: PathHop): string {
  const kind = h.device.kind === "ap" ? "AP" : h.device.kind;
  const name = h.device.name ?? h.device.serial ?? "?";
  return h.port_label ? `${kind} ${name} port ${h.port_label}` : `${kind} ${name}`;
}

/** The technical rows of one hop for the collapsible details. Empty values are left out. */
export function hopDetails(h: PathHop): [string, string][] {
  const rows: [string, string | null][] = [
    ["Hop", `${h.hop} ${h.name}`],
    ["Where", hopPlace(h)],
    ["Facing", h.peer ? `${h.peer.name ?? h.peer.serial ?? "?"}${h.peer.port_label ? ` port ${h.peer.port_label}` : h.peer.port ? ` ${h.peer.port}` : ""}` : null],
    ["Expected", h.expected],
    ["Actual", h.actual],
    ["Evidence", hopEvidence(h)],
    ["Why", h.reason],
    ["Link learned from", h.link_source ?? null],
    ["Code", h.code],
  ];
  return rows.filter((r): r is [string, string] => !!r[1]);
}

/** One line per VLAN of a post-rollout path check, worst first ("VLAN 1000: <sentence>"). */
export function rolloutPathLines(pc: RolloutPathCheck | null | undefined): { tone: PathTone; text: string }[] {
  if (!pc) return [];
  if (pc.status === "error") return [{ tone: "critical", text: `The VLAN path check failed: ${pc.error ?? "error"}` }];
  return Object.values(pc.vlans ?? {})
    .filter((c) => c.status === "broken" || c.status === "unknown")
    .sort((a, b) => (RANK[b.status] ?? 0) - (RANK[a.status] ?? 0) || (a.vlan ?? 0) - (b.vlan ?? 0))
    .map((c) => ({ tone: pathTone(c.status), text: c.summary }));
}

/** The query string of the check: exactly one of vlan / ssid. */
export function pathQuery(q: { vlan?: number | null; ssid?: string | null }): string {
  if (q.ssid) return `ssid=${encodeURIComponent(q.ssid)}`;
  return `vlan=${q.vlan ?? ""}`;
}

/** True when a warning list from a save / plan carries a path-check warning. */
export function hasPathWarning(warnings: { code: string }[] | null | undefined): boolean {
  return (warnings ?? []).some((w) => w.code === "vlan_path_broken" || w.code === "vlan_path_unknown");
}
